import { toast } from "sonner";
import { confirmDialog as confirm } from "@/components/ConfirmProvider";
import React, { useEffect, useState } from "react";
import {
  salesProspects, salesAddProspects, salesPatchProspect, salesDeleteProspect,
  salesPreviewNext, salesSendNext, salesRunNow, salesTemplates, salesSaveTemplates,
  salesSaveDraft, salesClearDraft, salesBrochure, salesPutBrochure, salesDeleteBrochure, salesPatchBrochure,
  salesResearch, salesResearchLeads, salesAddLead, salesDismissLead, salesLeadToCrm,
} from "@/lib/api";
import { Send, Loader2, X, Plus, Eye, Trash2, Play, Pause, Mail, PenLine, Search, Paperclip, Save } from "lucide-react";

/* Sales funnel — the Wishire Sales Agent. Load TPO/dean prospects, the agent
   runs a 3-step personalised drip (intro → ROI → breakup) with a daily cap,
   business-hours window, and stop-on-stage-change. Brevo sandbox until the
   API key is set, so the whole funnel is testable without sending. */

const STAGES = ["new", "sequenced", "replied", "demo", "pilot", "won", "lost", "no_reply", "unsubscribed"];
const STAGE_TONE = {
  new: "text-stone-300 border-stone-700", sequenced: "text-blue-300 border-blue-800",
  replied: "text-amber-300 border-amber-700", demo: "text-purple-300 border-purple-700",
  pilot: "text-cyan-300 border-cyan-700", won: "text-emerald-300 border-emerald-700",
  lost: "text-red-400 border-red-800", no_reply: "text-stone-500 border-stone-700",
  unsubscribed: "text-red-400 border-red-800",
};

export default function AdminSales() {
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [runMsg, setRunMsg] = useState(null);

  const load = () => salesProspects().then(({ data }) => setData(data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunMsg("running");
    try { const { data: r } = await salesRunNow(); setRunMsg(`Sent ${r.sent} email(s)${r.skipped_reason ? ` — ${r.skipped_reason}` : ""} · ${r.sent_today_total ?? 0}/${r.daily_cap ?? ""} today`); load(); }
    catch (e) { setRunMsg(e?.response?.data?.detail || "Run failed."); }
  };

  const doPreview = async (p) => {
    setBusyId(p.prospect_id); setPreview({ loading: true, college: p.college, prospect_id: p.prospect_id });
    try { const { data: d } = await salesPreviewNext(p.prospect_id); setPreview({ ...d, college: p.college, prospect_id: p.prospect_id }); }
    catch (e) { setPreview({ error: e?.response?.data?.detail || "Preview failed", college: p.college, prospect_id: p.prospect_id }); }
    finally { setBusyId(null); }
  };

  const doSend = async (p) => {
    if (!(await confirm({ title: "Please confirm", description: `Send step ${(p.next_step ?? 0) + 1} to ${p.email} now?`, danger: true }))) return;
    setBusyId(p.prospect_id);
    try { await salesSendNext(p.prospect_id); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusyId(null); }
  };

  const setStage = async (p, stage) => { await salesPatchProspect(p.prospect_id, { stage }); load(); };
  const togglePause = async (p) => { await salesPatchProspect(p.prospect_id, { paused: !p.paused }); load(); };
  const del = async (p) => {
    if (!(await confirm({ title: "Please confirm", description: `Delete prospect ${p.college}?`, danger: true }))) return;
    await salesDeleteProspect(p.prospect_id); load();
  };

  if (!data) return <div className="text-stone-500 py-16 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading…</div>;

  return (
    <div data-testid="admin-sales">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-stone-100">Sales funnel</h1>
          <p className="text-sm text-stone-400 mt-1">AI drip sequence to TPOs & deans — intro → ROI → breakup. Cap {data.daily_cap}/day, Mon–Fri 10:00–18:00 IST. {data.send_window_open ? "Window open." : "Window closed now."} Sent today: {data.sent_today}.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => setShowTemplates(true)} data-testid="sales-templates" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-stone-700 text-stone-200 text-sm hover:bg-stone-800"><PenLine size={14} /> Edit templates</button>
          <button onClick={runNow} data-testid="sales-run" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-stone-700 text-stone-200 text-sm hover:bg-stone-800"><Play size={14} /> Run due sends</button>
          <button onClick={() => setShowAdd(true)} data-testid="sales-add" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412]"><Plus size={15} /> Add prospects</button>
        </div>
      </div>
      {runMsg && <p className="mt-3 text-sm text-amber-300">{runMsg}</p>}

      {/* Funnel strip */}
      <div className="grid grid-cols-3 md:grid-cols-9 gap-2 mt-6">
        {STAGES.map((s) => (
          <div key={s} className="rounded-xl border border-stone-800 bg-[#1c1917] px-3 py-2 text-center">
            <p className="font-display text-xl text-stone-100">{data.funnel?.[s] ?? 0}</p>
            <p className="text-[9px] font-mono uppercase tracking-widest text-stone-500">{s.replace("_", " ")}</p>
          </div>
        ))}
      </div>

      <BrochureCard />
      <ResearchLeadsCard onAdded={load} />

      {showAdd && <AddProspectsModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {showTemplates && <TemplatesModal onClose={() => setShowTemplates(false)} />}
      {preview && <DraftEditorModal preview={preview} onClose={() => setPreview(null)} onChanged={load} />}

      <div className="mt-6 rounded-2xl border border-stone-800 bg-[#1c1917] overflow-x-auto">
        <table className="w-full text-sm text-stone-300">
          <thead><tr className="border-b border-stone-800 text-[10px] font-mono uppercase tracking-widest text-stone-500">
            {["College / contact", "Stage", "Next step", "Last email", "Actions"].map((h) => <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>)}
          </tr></thead>
          <tbody>
            {(data.prospects || []).map((p) => (
              <tr key={p.prospect_id} className="border-b border-stone-800/60 hover:bg-stone-900/40">
                <td className="px-4 py-3">
                  <p className="font-medium text-stone-100">{p.college}{p.city ? <span className="text-stone-500 font-normal"> · {p.city}</span> : ""}</p>
                  <p className="text-xs text-stone-500">{p.contact_name || "—"} {p.contact_role ? `(${p.contact_role})` : ""} · {p.email}</p>
                </td>
                <td className="px-4 py-3">
                  <select value={p.stage} onChange={(e) => setStage(p, e.target.value)} data-testid={`sales-stage-${p.prospect_id}`}
                    className={`bg-transparent border rounded-full px-2 py-1 text-xs ${STAGE_TONE[p.stage] || "text-stone-300 border-stone-700"}`}>
                    {STAGES.map((s) => <option key={s} value={s} className="bg-stone-900">{s.replace("_", " ")}</option>)}
                  </select>
                  {p.paused && <span className="ml-2 text-[10px] text-amber-400">paused</span>}
                </td>
                <td className="px-4 py-3 text-xs">
                  {p.next_step != null ? <>Step {p.next_step + 1}/3 · due {p.next_due_at ? new Date(p.next_due_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}</> : <span className="text-stone-600">complete</span>}
                  {p.custom_draft && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-300 border border-amber-800">custom draft</span>}
                </td>
                <td className="px-4 py-3 text-xs text-stone-500">
                  {p.last_email_at ? new Date(p.last_email_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}
                  {(p.history || []).length > 0 && <span className="text-stone-600"> · {(p.history || []).length} sent</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <button title="Preview next email" onClick={() => doPreview(p)} disabled={busyId === p.prospect_id || p.next_step == null} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-800 disabled:opacity-30"><Eye size={14} /></button>
                    <button title="Send next now" onClick={() => doSend(p)} disabled={busyId === p.prospect_id || p.next_step == null} data-testid={`sales-send-${p.prospect_id}`} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-800 disabled:opacity-30"><Send size={14} /></button>
                    <button title={p.paused ? "Resume sequence" : "Pause sequence"} onClick={() => togglePause(p)} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-800">{p.paused ? <Play size={14} /> : <Pause size={14} />}</button>
                    <button title="Delete" onClick={() => del(p)} className="p-1.5 rounded-lg text-red-500/70 hover:bg-red-900/30"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {(data.prospects || []).length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-stone-500">No prospects yet — click “Add prospects” to load your first list of TPOs.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-stone-500 flex items-center gap-1.5"><Mail size={12} />
        {data.email_sandbox
          ? "Delivery is SANDBOX (logged, not delivered) — safe to test the full funnel. Set BREVO_API_KEY to go live."
          : <span className="text-amber-500">Delivery is LIVE — sequenced prospects receive real emails. Set BREVO_SANDBOX_MODE=true to test safely.</span>}
      </p>
    </div>
  );
}

/* Per-college draft editor: rendered email preview (exact HTML that sends),
   editable subject/body, save-as-custom-draft (used verbatim on next send),
   or send immediately. */
function DraftEditorModal({ preview, onClose, onChanged }) {
  const [tab, setTab] = useState("preview"); // preview | edit
  const [subject, setSubject] = useState(preview.subject || "");
  const [body, setBody] = useState((preview.body || "").replace(/\n\n—[\s\S]*$/, ""));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setSubject(preview.subject || "");
    setBody((preview.body || "").replace(/\n\n—[\s\S]*$/, ""));
  }, [preview.subject, preview.body]);

  const saveDraft = async () => {
    setBusy(true); setMsg("");
    try { await salesSaveDraft(preview.prospect_id, subject, body); setMsg("Custom draft saved — it will be sent verbatim on this college's next send."); onChanged?.(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };
  const sendNow = async () => {
    if (!(await confirm({ title: "Please confirm", description: `Send this email to ${preview.college} now?`, danger: true }))) return;
    setBusy(true); setMsg("");
    try {
      await salesSaveDraft(preview.prospect_id, subject, body);   // ensure edits go out
      const { data } = await salesSendNext(preview.prospect_id);
      setMsg(`Sent — "${data.subject}" (${data.delivery}).`); onChanged?.();
    } catch (e) { setMsg(e?.response?.data?.detail || "Send failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-3xl rounded-2xl bg-[#1c1917] border border-stone-800 max-h-[92vh] overflow-y-auto" data-testid="draft-editor">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800 sticky top-0 bg-[#1c1917] z-10">
          <div>
            <h2 className="text-lg font-medium text-stone-100">{preview.college}</h2>
            {!preview.loading && !preview.error && <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500 mt-0.5">Step {(preview.step ?? 0) + 1} · {preview.step_name}</p>}
          </div>
          <div className="flex items-center gap-2">
            {!preview.loading && !preview.error && (
              <div className="flex rounded-full border border-stone-700 overflow-hidden text-xs">
                <button onClick={() => setTab("preview")} className={`px-3.5 py-1.5 ${tab === "preview" ? "bg-stone-100 text-stone-900 font-medium" : "text-stone-400"}`}>Email preview</button>
                <button onClick={() => setTab("edit")} data-testid="draft-tab-edit" className={`px-3.5 py-1.5 ${tab === "edit" ? "bg-stone-100 text-stone-900 font-medium" : "text-stone-400"}`}>Edit</button>
              </div>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-800"><X size={18} /></button>
          </div>
        </div>

        <div className="p-6">
          {preview.loading ? <p className="text-stone-400 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Researching the college &amp; drafting…</p>
            : preview.error ? <p className="text-red-400">{preview.error}</p>
            : (
              <>
                {(preview.research || []).length > 0 && (
                  <div className="mb-4 rounded-xl border border-stone-800 bg-stone-900/60 p-3">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-amber-400 flex items-center gap-1.5"><Search size={11} /> College research (feeds every draft)</p>
                    <ul className="mt-2 space-y-1">
                      {preview.research.map((b, i) => <li key={i} className="text-xs text-stone-400 flex gap-1.5"><span className="text-stone-600">•</span>{b}</li>)}
                    </ul>
                  </div>
                )}

                {tab === "preview" ? (
                  <>
                    <p className="text-xs text-stone-500 mb-1.5">Subject</p>
                    <p className="text-stone-100 font-medium mb-3">{subject}</p>
                    <div className="rounded-xl overflow-hidden border border-stone-700 bg-white">
                      <iframe title="email-preview" srcDoc={preview.html || ""} className="w-full" style={{ height: "480px", border: 0 }} />
                    </div>
                    <p className="mt-2 text-[11px] text-stone-500">This is the exact HTML the college receives — logo band, personalised line, feature chips, demo CTA and opt-out footer. Brochure attaches automatically if configured below.</p>
                  </>
                ) : (
                  <>
                    <label className="text-xs text-stone-500">Subject</label>
                    <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="draft-subject"
                      className="mt-1 w-full px-3 py-2.5 rounded-xl bg-stone-900 border border-stone-700 text-sm text-stone-100 focus:outline-none focus:border-stone-500" />
                    <label className="text-xs text-stone-500 mt-3 block">Body (plain text — paragraphs separated by blank lines; the design wraps around it)</label>
                    <textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)} data-testid="draft-body"
                      className="mt-1 w-full px-3 py-2.5 rounded-xl bg-stone-900 border border-stone-700 text-sm text-stone-200 leading-relaxed focus:outline-none focus:border-stone-500" />
                  </>
                )}

                {msg && <p className="mt-3 text-sm text-amber-300">{msg}</p>}
                <div className="mt-4 flex flex-wrap justify-end gap-3">
                  <button onClick={saveDraft} disabled={busy} data-testid="draft-save"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-700 text-stone-200 text-sm hover:bg-stone-800 disabled:opacity-60">
                    <Save size={14} /> Save custom draft
                  </button>
                  <button onClick={sendNow} disabled={busy} data-testid="draft-send"
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send now
                  </button>
                </div>
              </>
            )}
        </div>
      </div>
    </div>
  );
}

/* Brochure attachment — uploaded once, attached to chosen sequence steps. */
function BrochureCard() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = () => salesBrochure().then(({ data }) => setInfo(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3_000_000) { setMsg("File must be under 3MB."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true); setMsg("");
      try {
        const b64 = String(reader.result).split(",")[1];
        await salesPutBrochure({ name: f.name, mime: f.type || "application/pdf", b64, attach_steps: info?.attach_steps || [0] });
        setMsg("Brochure saved."); load();
      } catch (err) { setMsg(err?.response?.data?.detail || "Upload failed."); }
      finally { setBusy(false); }
    };
    reader.readAsDataURL(f);
  };

  const toggleStep = async (i) => {
    const cur = info?.attach_steps || [0];
    const next = cur.includes(i) ? cur.filter((s) => s !== i) : [...cur, i];
    try { await salesPatchBrochure(next); } catch (_) {}
    load();
  };

  return (
    <div className="mt-6 rounded-2xl border border-stone-800 bg-[#1c1917] p-5" data-testid="brochure-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-medium text-stone-100 flex items-center gap-2"><Paperclip size={14} className="text-amber-400" /> Brochure attachment</h3>
          <p className="text-xs text-stone-500 mt-1">
            {info?.exists
              ? <>Attached: <b className="text-stone-300">{info.name}</b> ({info.size_kb} KB) · goes with step{(info.attach_steps || [0]).map((s) => ` ${s + 1}`).join(",")}</>
              : "No brochure uploaded — emails send without an attachment."}
          </p>
          {info?.exists && (
            <div className="flex items-center gap-3 mt-2">
              {[0, 1, 2].map((i) => (
                <label key={i} className="inline-flex items-center gap-1.5 text-xs text-stone-400 cursor-pointer">
                  <input type="checkbox" checked={(info.attach_steps || [0]).includes(i)} onChange={() => toggleStep(i)} className="w-3.5 h-3.5 accent-[#C2410C]" />
                  Step {i + 1}
                </label>
              ))}
            </div>
          )}
          {msg && <p className="text-xs text-amber-300 mt-1">{msg}</p>}
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-700 text-stone-200 text-xs hover:bg-stone-800 cursor-pointer">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} {info?.exists ? "Replace PDF" : "Upload PDF"}
            <input type="file" accept="application/pdf" onChange={upload} className="hidden" data-testid="brochure-upload" />
          </label>
          {info?.exists && (
            <button onClick={async () => { await salesDeleteBrochure(); load(); }} className="p-2 rounded-full text-red-500/70 hover:bg-red-900/30" title="Remove brochure"><Trash2 size={14} /></button>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplatesModal({ onClose }) {
  const [steps, setSteps] = useState(null);
  const [defaults, setDefaults] = useState([]);
  const [offsets, setOffsets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    salesTemplates().then(({ data }) => { setSteps(data.steps.map((s) => ({ ...s }))); setDefaults(data.defaults); setOffsets(data.offsets_days || []); })
      .catch(() => setMsg("Failed to load templates."));
  }, []);

  const save = async () => {
    setSaving(true); setMsg("");
    try { await salesSaveTemplates(steps); setMsg("Saved — all future drafts use these briefs."); }
    catch (e) { setMsg(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };
  const resetDefaults = () => setSteps(defaults.map((s) => ({ ...s })));

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-2xl rounded-2xl bg-[#1c1917] border border-stone-800 p-6 max-h-[88vh] overflow-y-auto" data-testid="sales-templates-modal">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-stone-100">Email templates</h2>
            <p className="text-xs text-stone-500 mt-1">These briefs steer the AI writer. It personalises each email per college using the research card — the brief sets the angle, structure and ask of each step.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-800"><X size={18} /></button>
        </div>
        {!steps ? <p className="mt-6 text-stone-400 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</p> : (
          <div className="mt-5 space-y-5">
            {steps.map((s, i) => (
              <div key={i}>
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#C2410C] text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <input value={s.name} onChange={(e) => setSteps((x) => x.map((r, j) => j === i ? { ...r, name: e.target.value } : r))}
                    className="bg-transparent border border-stone-700 rounded-lg px-3 py-1.5 text-sm text-stone-100 font-medium flex-1" />
                  <span className="text-[10px] font-mono text-stone-500 shrink-0">day {offsets[i] ?? "—"}</span>
                </div>
                <textarea rows={4} value={s.brief} data-testid={`template-brief-${i}`}
                  onChange={(e) => setSteps((x) => x.map((r, j) => j === i ? { ...r, brief: e.target.value } : r))}
                  className="mt-2 w-full px-3 py-2.5 rounded-xl bg-stone-900 border border-stone-700 text-sm text-stone-300 leading-relaxed focus:outline-none focus:border-stone-500" />
              </div>
            ))}
            {msg && <p className="text-sm text-amber-300">{msg}</p>}
            <div className="flex items-center justify-between pt-2">
              <button onClick={resetDefaults} className="text-xs text-stone-400 hover:text-stone-200 underline underline-offset-2">Reset to defaults</button>
              <button onClick={save} disabled={saving} data-testid="templates-save"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
                {saving && <Loader2 size={14} className="animate-spin" />} Save templates
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const EMPTY_ROW = { college: "", email: "", contact_name: "", contact_role: "", city: "", website: "" };

function AddProspectsModal({ onClose, onSaved }) {
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
  const [bulk, setBulk] = useState(false);
  const [text, setText] = useState("");
  const [enroll, setEnroll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (i, k) => (e) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [k]: e.target.value } : row)));

  const collect = () => {
    if (bulk) {
      const out = [];
      for (const line of text.split("\n")) {
        const parts = line.split(",").map((s) => s.trim());
        if (parts.length < 2 || !parts[1].includes("@")) continue;
        out.push({ college: parts[0], email: parts[1], contact_name: parts[2] || undefined,
                   contact_role: parts[3] || undefined, city: parts[4] || undefined, website: parts[5] || undefined });
      }
      return out;
    }
    return rows
      .filter((r) => r.college.trim() && r.email.includes("@"))
      .map((r) => ({
        college: r.college.trim(), email: r.email.trim(),
        contact_name: r.contact_name.trim() || undefined, contact_role: r.contact_role.trim() || undefined,
        city: r.city.trim() || undefined, website: r.website.trim() || undefined,
      }));
  };

  const save = async () => {
    const payload = collect();
    if (!payload.length) { setErr("Fill at least one row with a college name and a valid email."); return; }
    setSaving(true); setErr("");
    try { const { data } = await salesAddProspects(payload, enroll); toast.success(`Added ${data.added} prospect(s), skipped ${data.skipped_duplicates} duplicate(s).`); onSaved?.(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed to add."); }
    finally { setSaving(false); }
  };

  const F = ({ i, k, ph, w, testid }) => (
    <input value={rows[i][k]} onChange={set(i, k)} placeholder={ph} data-testid={testid}
      className={`px-2.5 py-2 rounded-lg bg-stone-900 border border-stone-700 text-xs text-stone-200 focus:outline-none focus:border-stone-500 ${w || ""}`} />
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-3xl rounded-2xl bg-[#1c1917] border border-stone-800 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-stone-100">Add prospects</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => setBulk((b) => !b)} className="text-xs text-stone-400 hover:text-stone-200 underline underline-offset-2">{bulk ? "Form view" : "Bulk paste (CSV)"}</button>
            <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-800"><X size={18} /></button>
          </div>
        </div>

        {bulk ? (
          <>
            <p className="text-xs text-stone-500 mt-2">One per line: <span className="font-mono">College, email, contact name, role, city, website</span></p>
            <textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} data-testid="sales-add-text"
              placeholder={"GLA University, tpo@gla.ac.in, Dr. Sharma, TPO, Mathura, gla.ac.in"}
              className="mt-3 w-full px-3 py-2.5 rounded-xl bg-stone-900 border border-stone-700 text-sm text-stone-200 font-mono focus:outline-none focus:border-stone-500" />
          </>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-[1.4fr_1.4fr] gap-2 text-[10px] font-mono uppercase tracking-widest text-stone-500 px-0.5">
              <span>College &amp; contact</span><span>Details</span>
            </div>
            {rows.map((r, i) => (
              <div key={i} className="rounded-xl border border-stone-800 bg-stone-900/40 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <F i={i} k="college" ph="College name *" testid={`row-college-${i}`} />
                  <F i={i} k="email" ph="tpo@college.ac.in *" testid={`row-email-${i}`} />
                  <F i={i} k="contact_name" ph="Contact name (Dr. Sharma)" />
                  <F i={i} k="contact_role" ph="Role (TPO / Dean)" />
                  <F i={i} k="city" ph="City" />
                  <F i={i} k="website" ph="Website (college.ac.in) — powers AI research" />
                </div>
                {rows.length > 1 && (
                  <button onClick={() => setRows((x) => x.filter((_, j) => j !== i))} className="mt-2 text-[10px] text-red-500/70 hover:text-red-400">Remove row</button>
                )}
              </div>
            ))}
            <button onClick={() => setRows((r) => [...r, { ...EMPTY_ROW }])} data-testid="sales-add-row"
              className="inline-flex items-center gap-1.5 text-xs text-stone-300 border border-stone-700 rounded-full px-3.5 py-1.5 hover:bg-stone-800">
              <Plus size={12} /> Another college
            </button>
          </div>
        )}

        <label className="mt-4 flex items-center gap-2 text-sm text-stone-300 cursor-pointer">
          <input type="checkbox" checked={enroll} onChange={(e) => setEnroll(e.target.checked)} className="w-4 h-4 accent-[#C2410C]" />
          Start the email sequence immediately <span className="text-stone-500 text-xs">(recommended: leave off, preview drafts first)</span>
        </label>
        {err && <p className="mt-3 text-sm text-red-400">{err}</p>}
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-stone-700 text-stone-300 text-sm hover:bg-stone-800">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="sales-add-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
            {saving && <Loader2 size={14} className="animate-spin" />} Add prospects
          </button>
        </div>
      </div>
    </div>
  );
}

/* AI lead research: Apify Google search across a region → LLM extracts
   colleges + literally-present emails → review list → one click into the
   funnel (added paused, so a human always previews before an email fires). */
function ResearchLeadsCard({ onAdded }) {
  const [region, setRegion] = useState("");
  const [ctype, setCtype] = useState("engineering");
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => salesResearchLeads().then(({ data }) => setLeads(data.leads || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async () => {
    if (!region.trim()) { setMsg("Enter a region — e.g. Ghaziabad, Delhi NCR, Mathura."); return; }
    setBusy(true); setMsg("");
    try { const { data } = await salesResearch(region.trim(), ctype); setMsg(`Found ${data.found ?? 0} candidate(s), ${data.new ?? 0} new. ${data.note || ""}`); setOpen(true); load(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Research failed."); }
    finally { setBusy(false); }
  };

  const add = async (l) => {
    setRowBusy(l.lead_id);
    try { await salesAddLead(l.lead_id); setMsg(`${l.college} added to the funnel (paused — preview its draft before unpausing).`); load(); onAdded?.(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Could not add."); }
    finally { setRowBusy(null); }
  };
  const dismiss = async (l) => { await salesDismissLead(l.lead_id); load(); };
  const toCrm = async (l) => {
    setRowBusy(l.lead_id);
    try { const { data } = await salesLeadToCrm(l.lead_id); setMsg(data.existing ? `${l.college} is already in the Lead CRM.` : `${l.contact_name || l.designation || l.college} added to Lead CRM — call from Admin → Lead CRM.`); load(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Could not add to CRM."); }
    finally { setRowBusy(null); }
  };

  return (
    <div className="mt-4 rounded-2xl border border-stone-800 bg-[#1c1917] p-5" data-testid="research-card">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-medium text-stone-100 flex items-center gap-2"><Search size={14} className="text-amber-400" /> AI lead research</h3>
          <p className="text-xs text-stone-500 mt-1">Finds colleges in a region, then reads each college's own Training &amp; Placement page for the <b className="text-stone-300">placement head (TPO / Director of Placements)</b> — only designations that match, only emails and phones literally published. Never guessed.</p>
        </div>
        <div className="flex items-center gap-2">
          <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Region (e.g. Ghaziabad)" data-testid="research-region"
            className="px-3 py-2 rounded-lg bg-stone-900 border border-stone-700 text-xs text-stone-200 w-44 focus:outline-none focus:border-stone-500" />
          <select value={ctype} onChange={(e) => setCtype(e.target.value)} className="px-2 py-2 rounded-lg bg-stone-900 border border-stone-700 text-xs text-stone-300">
            {["engineering", "management", "pharmacy", "all"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <button onClick={run} disabled={busy} data-testid="research-run"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C2410C] text-white text-xs font-medium hover:bg-[#9a3412] disabled:opacity-60">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Find leads
          </button>
          {leads.length > 0 && <button onClick={() => setOpen((o) => !o)} className="text-xs text-stone-400 underline underline-offset-2">{open ? "Hide" : `Show ${leads.length}`}</button>}
        </div>
      </div>
      {msg && <p className="mt-2 text-xs text-amber-300">{msg}</p>}

      {open && leads.length > 0 && (
        <div className="mt-4 space-y-2">
          {leads.map((l) => (
            <div key={l.lead_id} className="flex items-center justify-between gap-3 rounded-xl border border-stone-800 bg-stone-900/40 px-3.5 py-2.5">
              <div className="min-w-0">
                <p className="text-sm text-stone-100 font-medium truncate">
                  {l.contact_name || <span className="text-stone-400">Name not published</span>}
                  {(l.designation || l.contact_role) && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-900/40 text-amber-300">{l.designation || l.contact_role}</span>}
                  {l.quality === "email+phone" && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-900/40 text-emerald-300">email + phone</span>}
                </p>
                <p className="text-xs text-stone-400 truncate">{l.college}{l.city ? ` · ${l.city}` : ""}</p>
                <p className="text-xs truncate mt-0.5 flex items-center gap-3">
                  {l.email ? <span className="text-emerald-400 font-mono">{l.email}</span> : <span className="text-stone-600">no published email</span>}
                  {l.phone ? <a href={`tel:${l.phone}`} className="text-sky-300 font-mono hover:underline">{l.phone}</a> : <span className="text-stone-600">no published phone</span>}
                </p>
                {l.evidence && <p className="text-[10px] text-stone-600 truncate">“{l.evidence}”{l.source_url && <span className="text-stone-700"> — {l.source_url}</span>}</p>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => toCrm(l)} disabled={rowBusy === l.lead_id} data-testid={`lead-crm-${l.lead_id}`}
                  title="Track in Lead CRM (call them)"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C2410C] text-white text-xs font-medium hover:bg-[#9a3412] disabled:opacity-30">
                  {rowBusy === l.lead_id ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Track in CRM
                </button>
                <button onClick={() => add(l)} disabled={rowBusy === l.lead_id || !l.email} data-testid={`lead-add-${l.lead_id}`}
                  title={l.email ? "Add to email funnel (paused)" : "No verified email — funnel needs one"}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-100 text-stone-900 text-xs font-medium hover:bg-white disabled:opacity-30">
                  Email funnel
                </button>
                <button onClick={() => dismiss(l)} className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-800" title="Dismiss"><X size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
